import type { MutablePlaybackState } from '../engine/animation/advancePlayback.js'
import type { RenderBackend as ParticleBudgetBackend } from '../engine/particles/particleBudget.js'
import type { ProjectDocumentV2 } from '../project/ProjectDocument.js'

export type RenderBackendKind = ParticleBudgetBackend
export type RenderBackendStatus = 'initializing' | 'ready' | 'context-lost' | 'disposed'
export type RenderInvalidationReason =
  | 'project' | 'layout' | 'viewport' | 'selection' | 'surface'
  | 'playback' | 'quality' | 'resource' | 'context' | 'backend'

export const RENDER_INVALIDATION_BITS = {
  project: 1 << 0,
  layout: 1 << 1,
  viewport: 1 << 2,
  selection: 1 << 3,
  surface: 1 << 4,
  playback: 1 << 5,
  quality: 1 << 6,
  resource: 1 << 7,
  context: 1 << 8,
  backend: 1 << 9,
} as const satisfies Record<RenderInvalidationReason, number>

export interface RenderSurfaceView {
  width: number
  height: number
  requestedDpr: number
  effectiveDpr: number
}

export type QualityTier = 'high' | 'balanced' | 'low' | 'minimum'

export interface RenderQualityView {
  tier: QualityTier
  particleBudget: number
}

export interface RenderParticleView {
  sourceCount: number
  renderCount: number
  indices: Uint32Array
  positionX: Float32Array
  positionY: Float32Array
  /** Previous prepared-frame positions used for directional motion blur. */
  previousPositionX?: Float32Array
  previousPositionY?: Float32Array
  size: Float32Array
  rotation: Float32Array
  alpha: Float32Array
}

export interface RenderViewportView {
  x: number
  y: number
  zoom: number
}

export interface MutableRenderFrameView {
  frameNumber: number
  invalidationMask: number
  deltaSeconds: number
  timeSeconds: number
  project: ProjectDocumentV2
  playback: MutablePlaybackState
  surface: RenderSurfaceView
  quality: RenderQualityView
  viewport: RenderViewportView
  particles: RenderParticleView
}

export type RenderFrameView = Readonly<MutableRenderFrameView>

export interface RenderBackend {
  readonly kind: RenderBackendKind
  getStatus(): RenderBackendStatus
  subscribeStatus(listener: (status: RenderBackendStatus) => void): () => void
  subscribeInvalidation(listener: () => void): () => void
  resize(surface: Readonly<RenderSurfaceView>): void
  render(frame: RenderFrameView): void
  dispose(): void
}
